import { useState } from 'preact/hooks';
import { createTip } from '../../api/tips';
import { Card } from '../../components/card';
import { Textarea } from '../../components/textarea';
import { Select } from '../../components/select';
import { Button } from '../../components/button';
import { Message } from '../../components/message';

interface BulkTipFormProps {
  onDone: (added: number, failed: number) => void;
  onCancel: () => void;
}

const FRAMEWORK_OPTIONS = [
  { value: '', label: 'General (no framework)' },
  { value: 'halal', label: 'Halal' },
  { value: 'allergen', label: 'Allergen' },
  { value: 'diabetic', label: 'Diabetic' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'heart-healthy', label: 'Heart-healthy' },
  { value: 'keto', label: 'Keto' },
  { value: 'renal', label: 'Renal' },
];

export function BulkTipForm({ onDone, onCancel }: BulkTipFormProps) {
  const [text, setText] = useState('');
  const [frameworkId, setFrameworkId] = useState('');
  const [adding, setAdding] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    const lines = text
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      setError('Enter at least one tip (one per line)');
      return;
    }

    setAdding(true);
    setError(null);
    const fw = frameworkId || null;
    let added = 0;
    let failed = 0;

    for (let i = 0; i < lines.length; i++) {
      setProgress(`Adding ${i + 1} of ${lines.length}...`);
      try {
        await createTip({ text: lines[i], framework_id: fw });
        added++;
      } catch {
        failed++;
      }
    }

    setAdding(false);
    setProgress('');
    onDone(added, failed);
  };

  return (
    <Card>
      <h2>Bulk Add Tips</h2>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
        One tip per line. Empty lines are skipped.
      </p>

      {error && <Message text={error} type="error" />}

      <div style={{ marginBottom: '12px' }}>
        <Textarea
          value={text}
          onChange={setText}
          rows={8}
          placeholder={"Tip one\nTip two\nTip three"}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Framework</label>
        <Select value={frameworkId} onChange={setFrameworkId} options={FRAMEWORK_OPTIONS} />
      </div>

      {progress && (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>{progress}</p>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        <Button onClick={handleAdd} disabled={adding}>
          {adding ? 'Adding...' : 'Add All'}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={adding}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
